import { dehydrate } from '@tanstack/react-query';
import { getQueryClient, isMobileRequest, useMobile } from 'common-nextjs';
import { createSession } from 'fetcher-session';
import React from 'react';
import { fetchAllPageViewData, fetchPageView } from '~/api/tiles';
import Layout from '~/components/Layout';
import PageView from '~/components/legos/PageView';
import { PageViewDataContext } from '~/contexts/PageViewDataContext';
import { CustomNextPage } from '~/typings/custom-next';
import { RailsPageView } from '~/typings/services/rails/tile';

interface Props {
  pageView?: RailsPageView;
  pageViewData?: any;
}

const AboutPage: CustomNextPage<Props> = ({ pageView, pageViewData }) => {
  const mobile = useMobile();

  const empoweringAthletes = (
    <div>
      <div
        className="text-primary-500 mb-2 font-bold uppercase md:mb-8"
        css={`
          font-size: 56px;
          line-height: 1em;

          @media (max-width: 1024px) {
            font-size: 48px;
          }
        `}
      >
        Empowering Athletes
      </div>

      <div className="divide-primary-500 -mb-4 flex flex-wrap divide-x-2 text-xl text-white md:justify-center md:text-2xl">
        <div className="mb-4 pr-3">TO PLAY</div>
        <div className="mb-4 px-3">TO SAVE</div>
        <div className="mb-4 px-3">TO EARN</div>
        <div
          className="mb-4 px-3"
          css={`
            @media (max-width: 480px) {
              border: none;
              padding-left: 0;
            }
          `}
        >
          TO HUSTLE
        </div>
        <div className="mb-4 pl-3">TO WIN</div>
      </div>
    </div>
  );

  const aboutUsParagraphs = (
    <div className="prose flex-1">
      <h1
        className="text-2xl font-bold uppercase"
        css={`
          color: black;
        `}
      >
        About Us
      </h1>

      <strong
        className="block"
        css={`
          color: black;
        `}
      >
        SidelineSwap connects athletes so they can buy, sell, and learn about
        new and used sports gear.
      </strong>

      <p className="my-4">
        We were founded by former college athletes who realized how difficult it
        was to find high-quality used sports gear online. Since launching the
        marketplace, hundreds of thousands of athletes have joined our community
        and contributed to our mission of making sports more affordable.
      </p>

      <p className="my-4">
        <strong className="block" css={'color: black;'}>
          Our promise to Sellers:
        </strong>{' '}
        We empower <em>every athlete, parent, coach, and small business</em> to
        sell. Whether you&apos;re just clearing out last season&apos;s gear,
        looking for a way to keep product on the field and out of a landfill, or
        building a sporting goods business, we&apos;re here to deliver{' '}
        <strong>
          <em>the best selling experience in sports</em>
        </strong>
        .
      </p>

      <p className="my-4">
        <strong className="block" css={'color: black;'}>
          Our promise to Buyers:
        </strong>{' '}
        We strive to offer customers the{' '}
        <strong>
          <em>best selection of inventory</em>
        </strong>
        , at the best prices, with all the information you need to confidently
        make your next purchase and perform at your best.
      </p>
    </div>
  );

  const sellerSpotlight = (
    <div className="flex-1">
      <h1
        className="mb-4 text-2xl font-bold uppercase"
        css={`
          color: black;
        `}
      >
        Seller Spotlight
      </h1>

      <iframe
        width="100%"
        src="https://www.youtube.com/embed/SX5Vrhra7KI"
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        css={`
          width: 100%;

          @media (max-width: 767px) {
            height: 194px;
          }

          @media (min-width: 767px) {
            height: 300px;
          }
        `}
      />
    </div>
  );

  return (
    <Layout title="About Us" container={false} excludeAppCta>
      <section
        aria-label="Hero"
        className="mb-4 flex items-center justify-center p-6 md:mb-6"
        css={`
          min-height: ${mobile ? 140 : 280}px;
          overflow: hidden;
          background: #000;
        `}
      >
        {empoweringAthletes}
      </section>
      <section className="container">
        {mobile ? (
          <React.Fragment>
            {aboutUsParagraphs}
            {sellerSpotlight}
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className="flex space-x-4 lg:space-x-8">
              {aboutUsParagraphs}
              {sellerSpotlight}
            </div>
          </React.Fragment>
        )}
      </section>
      <PageViewDataContext value={{ data: pageViewData, pageView }}>
        <PageView pageView={pageView} />
      </PageViewDataContext>
    </Layout>
  );
};

AboutPage.getInitialProps = async ctx => {
  const queryClient = getQueryClient();
  try {
    const pageView = await fetchPageView.call(ctx, 694);
    const pageViewData = await fetchAllPageViewData(
      pageView,
      isMobileRequest(ctx),
      undefined,
      queryClient,
      createSession(ctx),
    );

    return {
      pageView,
      pageViewData,
    };
  } catch (e) {}

  return {
    dehydratedState: dehydrate(queryClient),
  };
};

export default AboutPage;
